<app-hero-v2026
  eyebrow="About Investing for Catholics"
  heading="Financial science,"
  highlight="guided by Faith."
  lede="Investing for Catholics (IFC) is a fee-only fiduciary advisor serving Catholic organizations, religious institutes, and individuals with systematically managed, faith-consistent portfolios.">
  <a class="btn btn-primary" routerLink="." fragment="team">Meet Our Team</a>
  <a class="btn btn-outline-primary" routerLink="." fragment="serve">How We Serve Clients</a>
  <div class="about-trust" aria-label="Firm facts">
    <div><b>$6.02B<sup>&dagger;</sup></b>under management</div>
    <div><b>2,200+<sup>&dagger;</sup></b>IFA clients nationwide</div>
    <div><b>1999</b>fee-only since</div>
    <div><b>2009</b>IFC founded</div>
    <div>&dagger; As of December 31, 2025</div>
  </div>
  <div class="about-collage" aside aria-label="IFC team and events">
    <figure class="tall">
      <img src="https://services.ifa.com/art/images/1483_512x512_c_mary-brunson-2025.png" alt="Mary Brunson, Senior Vice President and Co-Founder of Investing for Catholics" width="512" height="512">
      <figcaption>Mary Brunson, AIF&reg; &mdash; Co-Founder</figcaption>
    </figure>
    <figure>
      <img src="https://services.ifa.com/art/images/3577_1024x576_c_ifc-info-booth.webp" alt="Investing for Catholics information booth at a Catholic conference" width="1024" height="576" loading="lazy">
      <figcaption>IFC on the road</figcaption>
    </figure>
    <figure>
      <img src="https://services.ifa.com/art/images/3577_1024x576_c_ifc-info-table-2026.webp" alt="IFC team at an information table, 2026" width="1024" height="576" loading="lazy">
      <figcaption>Meeting stewards in person</figcaption>
    </figure>
  </div>
</app-hero-v2026>

<div class="about-page">

  <section class="about-story" id="story">
    <div class="container">
      <div class="story-grid">
        <div>
          <span class="eyebrow">Our Story</span>
          <h2>Built for Catholic stewards who want both prudence and principle.</h2>
          <p>Investing for Catholics is a division of Index Fund Advisors, Inc. (IFA), a fee-only registered investment advisor<sup>**</sup> based in Irvine, California. IFA was founded in 1999 on the conviction that evidence-based, systematically managed portfolios are a more prudent approach for investors than speculation and stock picking.</p>
          <p>In 2009, Mary Brunson co-founded IFC to bring that same discipline to Catholic investors who wanted their portfolios to reflect the teachings of the Church. Today IFC works with religious orders, dioceses, endowments, foundations, retirement plans, and individual families across the country.</p>
          <p class="pull">Our aim is to enable stewards to prudently balance faith-consistent investing with fiduciary best practices.</p>
          <p>IFC portfolios are built from underlying funds that apply Catholic values screens, and each client relationship is led by an Investment Advisor. As a registered investment adviser, IFA is a fiduciary to its advisory clients and is obligated to act in their best interest.</p>
          <p class="fine">** SEC registration does not constitute an endorsement of the firm by the Commission, nor does it indicate that the adviser has attained a particular level of skill or ability. Advisory services are offered through Index Fund Advisors, Inc., a registered investment adviser, only pursuant to a written advisory agreement. Target date allocations become more conservative over time but are not guaranteed; investments remain subject to market risk, including near and after the target date.</p>
        </div>
        <div>
          <ol class="timeline" aria-label="Milestones">
            <li>
              <time>1999</time>
              <h3>Index Fund Advisors founded</h3>
              <p>IFA begins as a fee-only advisory firm applying academic financial science to client portfolios.</p>
            </li>
            <li>
              <time>2009</time>
              <h3>Investing for Catholics launches</h3>
              <p>Co-founded by Mary Brunson as a division of IFA, focused on Catholic faith-consistent investing for institutions and individuals.</p>
            </li>
            <li>
              <time>Ongoing</time>
              <h3>Serving the Catholic community</h3>
              <p>Presenting on faith-aligned investment policy at conferences, publishing research, and co-authoring <em>Tending the Flock: Shepherding Catholic Retirement Plans</em>.</p>
            </li>
            <li>
              <time>Today</time>
              <h3>$6.02 billion under management<sup>&dagger;</sup></h3>
              <p>IFA serves 2,200+ clients nationwide<sup>&dagger;</sup>, with IFC continuing to grow its work with Catholic organizations and families.</p>
            </li>
          </ol>
        </div>
      </div>
    </div>
  </section>

  <section class="about-principles" id="principles">
    <div class="container">
      <div class="section-head center">
        <span class="eyebrow">What Sets IFC Apart</span>
        <h2>Four commitments behind our client relationships</h2>
        <p>The foundation of how we work with clients &mdash; whether a diocese, a foundation, or a family.</p>
      </div>
      <div class="prin-grid">
        <div class="prin">
          <span class="chip" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M12 3l7 3v5c0 5-3.5 8.5-7 10-3.5-1.5-7-5-7-10V6l7-3z"/><path d="M9 12l2 2 4-4"/></svg></span>
          <h3>Fiduciary</h3>
          <p>As a registered investment adviser, IFA owes a fiduciary duty to its advisory clients, including a duty to act in their best interest.</p>
          <a routerLink="/strategy">Learn about our approach</a>
        </div>
        <div class="prin">
          <span class="chip" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><circle cx="12" cy="8" r="4"/><path d="M4 21v-2a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v2"/></svg></span>
          <h3>Partnership with Purpose</h3>
          <p>Your dedicated IFC Investment Advisor will take the time to understand your specific goals, needs and relevant standard of care to help shape an investment approach for you or your organization.</p>
          <a routerLink="." fragment="team">Meet our team</a>
        </div>
        <div class="prin">
          <span class="chip" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M12 3v18M6 8h12"/><path d="M8 21h8"/></svg></span>
          <h3>Faith-Consistent</h3>
          <p>Portfolios are built from underlying funds that apply Catholic values screens consistent with the USCCB Socially Responsible Investment Guidelines.</p>
          <a routerLink="/strategy">How screening works</a>
        </div>
        <div class="prin">
          <span class="chip" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M3 17l5-5 4 4 8-8"/><path d="M15 8h5v5"/></svg></span>
          <h3>Systematically Managed</h3>
          <p>Globally diversified index portfolios grounded in academic financial research, including research recognized by the Nobel Committee, rebalanced with discipline &mdash; not forecasts or market timing.</p>
          <a routerLink="/portfolios">Explore our portfolios</a>
        </div>
      </div>
    </div>
  </section>

  <section class="about-serve" id="serve">
    <div class="container">
      <div class="serve-intro">
        <div>
          <span class="eyebrow">How IFC Serves Clients</span>
          <h2>A dedicated advisor. A full team behind you.</h2>
          <p>Your <strong>IFC Investment Advisor</strong> oversees and coordinates your investment process. From initial discussion through ongoing management, individual and institutional investors have access to IFC's investment education program, custom asset allocations designed to address a range of risk levels, ongoing portfolio management, and reporting.</p>
        </div>
        <div class="quarterback">
          <span class="eyebrow">Your Point of Contact</span>
          <h3>Your IFC Investment Advisor quarterbacks the process</h3>
          <p>Your advisor works with a dedicated investment management team so that the key parts of your relationship &mdash; policy, onboarding, trading, reporting, compliance &mdash; are supported by dedicated team members.</p>
          <div class="who">
            <img src="https://services.ifa.com/art/images/1483_512x512_c_mary-brunson-2025.png" alt="" width="56" height="56" loading="lazy">
            <div>
              <b>Mary Brunson, AIF&reg;</b>
              <span>Senior Vice President, Investment Advisor and Co&#8209;Founder</span>
            </div>
          </div>
        </div>
      </div>

      <div class="team-grid">
        <div class="team-fn">
          <span class="num">01</span>
          <h3>Investment Committee</h3>
          <p>Our internal investment committee establishes investment policy, strategy, and processes for selecting and monitoring investments, and oversees the development and ongoing management of IFC's custom portfolio implementations, including Catholic Values Target Date Strategies and risk-based portfolios.</p>
          
        </div>
        <div class="team-fn">
          <span class="num">02</span>
          <h3>Client Servicing Team</h3>
          <p>Our experienced client service professionals offer personal, attentive support throughout your relationship with IFC. They manage your account administration, including account opening, documentation, onboarding, account updates, reporting, withdrawals, and deposits.</p>
        </div>
        <div class="team-fn">
          <span class="num">03</span>
          <h3>Investor Education</h3>
          <p>Our team is committed to helping you understand the thinking behind your investments. Investment Advisors answer questions through one-on-one conversations, while our investment team contributes research and insights to articles, videos, and presentations on systematically managed, faith-consistent portfolios.</p>
        </div>
        <div class="team-fn">
          <span class="num">04</span>
          <h3>Portfolio Management Team</h3>
          <p>Our portfolio management team conducts investment research and analyzes portfolio positions to inform ongoing management. They execute trades, oversee cash flows, and rebalance, while seeking to keep costs reasonable. Our IT team maintains the systems that support this work and manages data protection.</p>
        </div>
        <div class="team-fn">
          <span class="num">05</span>
          <h3>Retirement Plan Services</h3>
          <p>The Retirement Plan Services team works diligently to support 403(b), 401(k), 457(b), and other defined contribution plan sponsors in following fiduciary best practices. Plan sponsors retain responsibility for the prudent selection and monitoring of their service providers.</p>
        </div>
        <div class="team-fn">
          <span class="num">06</span>
          <h3>Executive Leadership</h3>
          <p>Our executive leadership team provides management oversight and direction for the firm. Working with our compliance professionals, they oversee operations, review policies and procedures, and support a culture of accountability, ethical conduct, and regulatory compliance.</p>
        </div>
      </div>

    </div>
  </section>

  <section class="about-team" id="team">
    <div class="container">
      <div class="section-head">
        <span class="eyebrow">Our Team</span>
        <h2>The people behind Investing for Catholics</h2>
        <p>A mission-driven team providing personal service, backed by the full resources of Index Fund Advisors.</p>
      </div>

      <h3 class="group">IFC Investment Advisors</h3>
      @for (advisor of advisors(); track advisor.url) {
        <div class="featured">
          <img [src]="advisor.imageUrl" [alt]="advisor.fullName" width="512" height="512" loading="lazy">
          <div class="body">
            <h4>{{ advisor.fullName }}@if (advisor.credentials) {, {{ advisor.credentials }}}</h4>
            <div class="role">{{ advisor.title }} <small>&middot; Investing for Catholics</small></div>
            <div class="bio" [innerHTML]="advisor.bioExcerpt"></div>
            <div class="links">
              <a routerLink="/about/{{ advisor.url }}">Full biography &rarr;</a>
              @if (advisor.phone) {
                <a [href]="advisor.phoneHref">{{ advisor.phone }}</a>
              }
            </div>
          </div>
        </div>
      }
    </div>
    <div id="aboutUsInfo">
      <app-managers-list></app-managers-list>
      <app-css-list></app-css-list>

      <div class="container">
        <figure class="team-photo">
          <img src="https://services.ifa.com/art/images/3470_1427x667_c_ifa-company-photo-2025_q95.webp" alt="Group portrait of 40 IFA team members" width="1956" height="915" loading="lazy">
          <figcaption>The IFA Team</figcaption>
        </figure>
      </div>
    </div>
  </section>

  <section class="about-credo" id="credo">
    <div class="container">
      <div class="section-head center">
        <span class="eyebrow">IFC's Investing Credo</span>
        <h2>Five principles that guide IFC's investment decisions</h2>
      </div>
      <div class="credo-grid">
        <div class="credo-item">
          <h3>Markets Work</h3>
          <p>IFC's research is based on the widely accepted Efficient Market Hypothesis, whose founder, Eugene Fama, was awarded the 2013 Nobel Prize in Economics.</p>
        </div>
        <div class="credo-item">
          <h3>Investing Science Shows the Way</h3>
          <p>IFC's strategies are based on Modern Portfolio Theory and the Fama-French Five-Factor Model for equities and Two-Factor Model for fixed income.</p>
        </div>
        <div class="credo-item">
          <h3>Diversification is Essential</h3>
          <p>IFC's portfolios seek to provide global diversification across U.S., International and Emerging Markets, plus Global Real Estate and Fixed Income.</p>
        </div>
        <div class="credo-item">
          <h3>Prudent, Faith-Consistent Investing is Possible</h3>
          <p>Based on IFC's research, properly constructed, diversified screened portfolios with Fama-French factor exposure are expected to vary little in long-term performance from similarly constructed non-screened portfolios. Actual results may differ, and screened portfolios may underperform or outperform non-screened portfolios in any given period.</p>
        </div>
        <div class="credo-item">
          <h3>Discipline and Patience Guide Our Approach</h3>
          <p>IFC's systematically managed strategy seeks to deliver risk-appropriate returns in a cost-conscious, diversified, and prudent manner.</p>
        </div>
      </div>
    </div>
  </section>

  <section class="about-quote">
    <div class="container">
      <blockquote>&ldquo;It must be remembered that the market does not exist in the pure state. It is shaped by the cultural configurations which define it and give it direction.&rdquo;</blockquote>
      <cite>&mdash; Pope Benedict XVI *</cite>
      <span class="fine">* Provided for information purposes only; not an endorsement, recommendation, or guarantee of any financial product, service, or advisor.</span>
    </div>
  </section>

  <section class="about-cta" id="talk">
    <div class="container">
      <div class="cta-inner">
        <div>
          <span class="eyebrow">Let's Talk</span>
          <h2>Speak with an IFC Investment Advisor</h2>
          <p>Whether you steward an organization's assets or your family's savings, start with a conversation.</p>
          <p class="contact-lines"><b>Call</b> <a href="tel:+18888155025">888.815.5025</a> &nbsp;&middot;&nbsp; <b>Visit</b> Irvine, California</p>
        </div>
        <div class="actions">
          <a class="btn btn-primary" routerLink="/open-account">Contact Us</a>
          <a class="btn btn-outline-primary" routerLink="/open-account">Open an Account</a>
        </div>
      </div>
    </div>
  </section>

</div>
<!-- 

<section id="about">
  <div class="container">
      <div class="row">
          <div class="col-12">
              <h1>About Investing for Catholics</h1>
          </div>
      </div>
  </div>
</section>

<section class="section" id="aboutUsInfo">
  <div class="container">
    <div class="row">
      <div class="col-sm-12">
        <h2>How IFC Serves Clients</h2>
        <p>Your <strong>IFC Investment Advisor</strong> oversees and coordinates your investment process. From initial discussion through ongoing management, individual and institutional investors have access to IFC’s investment education program, custom asset allocations designed to address a range of risk levels, ongoing portfolio management and reporting. Your investment advisor quarterbacks the process and works with a dedicated investment management team (shown below) that includes:</p>

        <ul>
          <li>
            An analytical <strong>investment committee</strong> that establishes investment policy, strategy, process, selection, and monitoring of investments, as well as development of IFC’s managed portfolio implementations.
          </li>
          <li>
            A professional <strong>Client Servicing Team</strong> to open accounts, prepare documents, navigate the client on-boarding process, and assist with administrative requests including updating account information, reporting requests, withdrawals and deposits.
          </li>
          <li>
            An <strong>Operations Team</strong> focused on client communications, back office support, IT systems and data protection, accounting, compliance and legal.
          </li>
          <li>
            A qualified <strong>Portfolio Management Team</strong> to manage your investment portfolio, execute trades, oversee cash flows, and rebalance, while seeking to keep costs reasonable.
          </li>
          <li>
            Through its sister division, <strong>IFA Taxes</strong>, IFC provides individuals, businesses, trusts and non-profit entities across the United States a wide range of tax planning, tax preparation and accounting services.*
          </li>
          <li>
            Referring relationships with various <strong>independent experts and industry professionals</strong> to assist with matters such as estate planning, tax, financial planning, insurance needs, 529 plans, retirement readiness and planning, charitable giving, as well as ERISA compliance, custodial account setup for multiple beneficiary accounts and distribution, account auditing, record-keeping and valuation for pooled assets, and more.
          </li>
        </ul>

        <p><small>*IFA Taxes does not provide auditing or attestation services and therefore is not a licensed CPA firm. IRS Circular 230 Disclosure: To ensure compliance with imposed by the IRS, we inform you that any U.S. Federal tax advice contained in this communication is not intended or written to be used, and cannot be used, for the purpose of (i) avoiding penalties under the Internal Revenue Code or (ii) promoting, marketing or recommending to another party any transaction or matter herein.</small></p>
      </div>
    </div>
    <router-outlet></router-outlet>
   
      <app-advisor-list></app-advisor-list>

      <app-managers-list></app-managers-list>

      <app-css-list></app-css-list>

</div>
</section> -->